import React from 'react';

/**
 * Closes an open menu the way a dropdown is expected to close: on a press anywhere outside it and on
 * Escape. The container is the menu together with the control that opens it, so pressing that
 * control is left to its own toggle, and every panel nested inside counts as inside.
 *
 * Escape hands the keyboard focus back to the control that opened the menu, so a keyboard reader
 * goes on from where they were rather than from the top of the page. A press outside leaves the focus
 * where the press put it.
 */
export function useDismissOnOutsideClick({
  open,
  containerRef,
  triggerRef,
  onDismiss,
}: {
  open: boolean;
  containerRef: React.RefObject<HTMLElement | null>;
  /** The control that opened the menu, which Escape returns the focus to. */
  triggerRef: React.RefObject<HTMLElement | null>;
  onDismiss: () => void;
}): void {
  const onDismissRef = React.useRef(onDismiss);
  onDismissRef.current = onDismiss;

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const container = containerRef.current;
      if (container && event.target instanceof Node && !container.contains(event.target)) {
        onDismissRef.current();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      // Only focus the menu held, or that the page lost, goes back; a reader who moved on keeps theirs.
      const focused = document.activeElement;
      const focusInMenu = !focused || focused === document.body || !!containerRef.current?.contains(focused);
      onDismissRef.current();
      if (focusInMenu) {
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [containerRef, open, triggerRef]);
}
